import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { BANKING_PERMISSIONS } from "@folio/shared/permissions";
import { usePermissions } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { bankingApi } from "../api";
import { useBankingRequest } from "../hooks/useBankingRequest";
import type {
  BankAccountDto,
  BankingBatchSummaryDto,
  BankingBatchDetailDto,
  BankingPageDto,
  BankMovementDto,
} from "../types";
import {
  BankingError,
  BankingPanel,
  BankingPager,
  BankingTotals,
} from "./BankingShared";
import { BankingImportDetail } from "./BankingImportDetail";

export function BankingWorkspace() {
  const [params, setParams] = useSearchParams();
  const accountId = params.get("account") ?? "";
  const [accountInput, setAccountInput] = useState(accountId);
  const [accounts, setAccounts] = useState<BankAccountDto[]>([]);
  const [nextAccountCursor, setNextAccountCursor] = useState<string | null>(
    null,
  );
  const [instructions, setInstructions] = useState<string | null>(null);
  const { hasPermission, isLoading } = usePermissions();
  const readAccounts =
    !isLoading && hasPermission(BANKING_PERMISSIONS.accountRead);
  const read = !isLoading && hasPermission(BANKING_PERMISSIONS.importRead);
  const accountRequest = useBankingRequest();
  const instructionRequest = useBankingRequest();
  const download = useBankingRequest();
  const { run: runAccounts } = accountRequest;
  const { run: runInstructions } = instructionRequest;
  const [delimiter, setDelimiter] = useState<"," | ";">(",");
  useEffect(() => {
    if (readAccounts)
      void runAccounts(
        (signal) => bankingApi.accounts(undefined, signal),
        (page) => {
          setAccounts(page.items);
          setNextAccountCursor(page.nextCursor);
        },
      );
  }, [readAccounts, runAccounts]);
  useEffect(() => {
    if (read)
      void runInstructions(bankingApi.instructions, (result) =>
        setInstructions(result.instructions),
      );
  }, [read, runInstructions]);
  useEffect(() => {
    setAccountInput(accountId);
  }, [accountId]);
  const select = (value: string) => {
    setAccountInput(value);
    setParams(value ? { account: value } : {});
  };
  const downloadTemplate = (format: "csv" | "xlsx") =>
    void download.run(
      (signal) => bankingApi.template(format, delimiter, signal),
      (blob) => {
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = `movimientos.${format}`;
        anchor.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      },
      read,
    );
  return (
    <div className="space-y-6">
      <BankingPanel title="Cuenta seleccionada">
        {readAccounts && (
          <>
            <Label htmlFor="bank-select">Cuentas de la organización</Label>
            <select
              id="bank-select"
              className="h-10 w-full rounded border bg-background px-3"
              value={accounts.some((a) => a.id === accountId) ? accountId : ""}
              onChange={(e) => select(e.target.value)}
            >
              <option value="">Selecciona una cuenta</option>
              {accounts.map((a) => (
                <option value={a.id} key={a.id}>
                  {a.bankLabel} · {a.name} · {a.accountNumber} · {a.currency}
                  {!a.active && " (inactiva)"}
                </option>
              ))}
            </select>
            {nextAccountCursor && (
              <Button
                variant="outline"
                disabled={accountRequest.pending}
                onClick={() =>
                  void accountRequest.run(
                    (signal) => bankingApi.accounts(nextAccountCursor, signal),
                    (page) => {
                      setAccounts((a) => [...a, ...page.items]);
                      setNextAccountCursor(page.nextCursor);
                    },
                  )
                }
              >
                Más cuentas
              </Button>
            )}
            <BankingError error={accountRequest.error} />
          </>
        )}
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            select(accountInput.trim());
          }}
        >
          <div className="min-w-64 flex-1 space-y-2">
            <Label htmlFor="bank-id">ID de cuenta bancaria</Label>
            <Input
              id="bank-id"
              value={accountInput}
              onChange={(e) => setAccountInput(e.target.value)}
            />
          </div>
          <Button type="submit">Seleccionar cuenta</Button>
          <Button type="button" variant="outline" onClick={() => select("")}>
            Limpiar selección
          </Button>
        </form>
        {!readAccounts && (
          <p>
            Selecciona una cuenta desde el historial o ingresa el ID facilitado
            por quien configura las cuentas bancarias.
          </p>
        )}
        {accountId ? (
          <p className="break-all">
            Cuenta elegida: {accountId}. El servidor verifica que pertenezca a
            esta organización.
          </p>
        ) : (
          <p>
            Selecciona exactamente una cuenta para cargar archivos y consultar
            movimientos.
          </p>
        )}
        {readAccounts && (
          <Button variant="link" asChild>
            <Link to="/configuracion/cuentas-bancarias">
              Configurar cuentas bancarias
            </Link>
          </Button>
        )}
      </BankingPanel>
      <BankingPanel title="Plantilla e instrucciones">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            disabled={download.pending}
            onClick={() => downloadTemplate("xlsx")}
          >
            Descargar XLSX
          </Button>
          <Button
            variant="outline"
            disabled={download.pending}
            onClick={() => downloadTemplate("csv")}
          >
            Descargar CSV
          </Button>
          <div className="flex flex-wrap items-center gap-2">
            <Label htmlFor="bank-delimiter">Separador CSV</Label>
            <select
              id="bank-delimiter"
              className="rounded border bg-background p-2"
              value={delimiter}
              onChange={(e) => setDelimiter(e.target.value as "," | ";")}
            >
              <option value=",">Coma</option>
              <option value=";">Punto y coma</option>
            </select>
          </div>
        </div>
        <BankingError error={download.error} />
        {instructions === null && !instructionRequest.error && (
          <p>Cargando instrucciones…</p>
        )}
        <BankingError error={instructionRequest.error} />
        {instructions !== null && (
          <ul className="list-disc space-y-2 pl-5">
            {instructions
              .split(/\r\n|\r|\n/)
              .filter((paragraph) => paragraph.trim().length > 0)
              .map((paragraph, index) => (
                <li className="whitespace-pre-wrap break-words" key={index}>
                  {paragraph}
                </li>
              ))}
          </ul>
        )}
      </BankingPanel>
      <AccountWorkspace
        key={accountId}
        accountId={accountId}
        onSelectAccount={select}
      />
    </div>
  );
}

function AccountWorkspace({
  accountId,
  onSelectAccount,
}: {
  accountId: string;
  onSelectAccount: (id: string) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [batch, setBatch] = useState<BankingBatchDetailDto | null>(null);
  const [historyRevision, setHistoryRevision] = useState(0);
  const upload = useBankingRequest();
  const { hasPermission, isLoading } = usePermissions();
  const write = !isLoading && hasPermission(BANKING_PERMISSIONS.importWrite);
  return (
    <div className="space-y-6">
      <BankingPanel title="Importar movimientos bancarios">
        <p>
          El archivo usa la cuenta y moneda elegidas. Revisa todas las filas y
          decide cada candidato antes de confirmar el archivo completo.
        </p>
        {!write && <p>Solo consulta: no tienes permiso de importación.</p>}
        {write && (
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (file && accountId && !upload.pending) {
                setBatch(null);
                void upload.run(
                  (signal) => bankingApi.preview(accountId, file, signal),
                  (result) => {
                    setBatch(result);
                    setHistoryRevision((n) => n + 1);
                  },
                  write,
                );
              }
            }}
          >
            <Label htmlFor="bank-file">Archivo XLSX o CSV</Label>
            <Input
              id="bank-file"
              type="file"
              accept=".xlsx,.csv"
              disabled={!accountId || upload.pending}
              onChange={(e) => {
                setFile(e.target.files?.[0] ?? null);
                setBatch(null);
              }}
            />
            <Button
              type="submit"
              disabled={!accountId || !file || upload.pending}
            >
              Obtener vista previa
            </Button>
          </form>
        )}
        <BankingError error={upload.error} />
      </BankingPanel>
      {batch && (
        <BankingImportDetail
          key={batch.id}
          initial={batch}
          onChanged={() => setHistoryRevision((n) => n + 1)}
        />
      )}
      <BankingHistory
        key={historyRevision}
        accountId={accountId}
        onSelectAccount={onSelectAccount}
      />
      {accountId && (
        <BankingMovements
          key={`${accountId}:${historyRevision}`}
          accountId={accountId}
        />
      )}
    </div>
  );
}

function BankingHistory({
  accountId,
  onSelectAccount,
}: {
  accountId: string;
  onSelectAccount: (id: string) => void;
}) {
  const [page, setPage] =
    useState<BankingPageDto<BankingBatchSummaryDto> | null>(null);
  const request = useBankingRequest();
  const { run } = request;
  useEffect(() => {
    void run(
      (signal) => bankingApi.history(accountId || undefined, undefined, signal),
      setPage,
    );
  }, [run, accountId]);
  const load = (cursor?: string) =>
    void request.run(
      (signal) => bankingApi.history(accountId || undefined, cursor, signal),
      setPage,
    );
  return (
    <BankingPanel title="Historial de importaciones">
      <BankingError error={request.error} />
      {request.pending && <p>Cargando historial…</p>}
      {page?.items.length === 0 && <p>Sin importaciones.</p>}
      {page?.items.map((b) => (
        <article key={b.id} className="space-y-1 rounded border p-3">
          <Link
            className="font-medium underline"
            to={`/contabilidad/bancos/importaciones/${b.id}`}
          >
            {b.filename}
          </Link>
          <p>
            {b.account.bankLabel} · {b.account.accountNumber} ·{" "}
            {b.account.currency} · Estado {b.status}
          </p>
          <p>
            Filas {b.counts.source} · Válidas {b.counts.valid} · Inválidas{" "}
            {b.counts.invalid} · Aceptadas {b.counts.accepted} · Descartadas{" "}
            {b.counts.discarded}
          </p>
          <BankingTotals
            label={b.confirmedTotals ? "Confirmados" : "Aceptados"}
            totals={b.confirmedTotals ?? b.acceptedTotals}
            currency={b.account.currency}
          />
          <p className="text-sm">
            Creado por {b.createdBy} · {b.createdAt} · Actualizado {b.updatedAt}{" "}
            · Confirmado por {b.confirmedBy ?? "—"} · {b.confirmedAt ?? "—"}
          </p>
          <Button
            variant="outline"
            onClick={() => onSelectAccount(b.bankAccountId)}
          >
            Seleccionar cuenta {b.account.accountNumber}
          </Button>
        </article>
      ))}
      <BankingPager
        pending={request.pending}
        next={page?.nextCursor}
        onReset={() => load()}
        onNext={() => load(page?.nextCursor ?? undefined)}
      />
    </BankingPanel>
  );
}

function BankingMovements({ accountId }: { accountId: string }) {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [filters, setFilters] = useState({ start: "", end: "" });
  return (
    <BankingPanel title="Consulta de movimientos">
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          setFilters({ start, end });
        }}
      >
        <div className="space-y-2">
          <Label htmlFor="bank-start">Desde (inclusive)</Label>
          <Input
            id="bank-start"
            type="date"
            value={start}
            onChange={(e) => setStart(e.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="bank-end">Hasta (inclusive)</Label>
          <Input
            id="bank-end"
            type="date"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
          />
        </div>
        <Button type="submit">Consultar movimientos</Button>
      </form>
      <MovementResults
        key={`${filters.start}:${filters.end}`}
        accountId={accountId}
        start={filters.start}
        end={filters.end}
      />
    </BankingPanel>
  );
}
function MovementResults({
  accountId,
  start,
  end,
}: {
  accountId: string;
  start: string;
  end: string;
}) {
  const [page, setPage] = useState<BankingPageDto<BankMovementDto> | null>(
    null,
  );
  const request = useBankingRequest();
  const { run } = request;
  useEffect(() => {
    void run(
      (signal) =>
        bankingApi.movements(accountId, start, end, undefined, signal),
      setPage,
    );
  }, [run, accountId, start, end]);
  const load = (cursor?: string) =>
    void request.run(
      (signal) => bankingApi.movements(accountId, start, end, cursor, signal),
      setPage,
    );
  return (
    <div className="space-y-3">
      <BankingError error={request.error} />
      {request.pending && <p>Cargando movimientos…</p>}
      {page?.items.length === 0 && <p>Sin movimientos para estos filtros.</p>}
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr>
              {[
                "Fecha",
                "Descripción",
                "Referencia",
                "Cargo",
                "Abono",
                "Moneda histórica",
                "Cuenta histórica",
                "Contraparte",
                "Origen",
              ].map((h) => (
                <th className="p-2" key={h}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {page?.items.map((m) => (
              <tr key={m.id} className="border-t">
                <td className="p-2">{m.date}</td>
                <td className="p-2">{m.description}</td>
                <td className="p-2">{m.reference ?? "—"}</td>
                <td className="p-2">{m.debit}</td>
                <td className="p-2">{m.credit}</td>
                <td className="p-2">{m.currency}</td>
                <td className="p-2">
                  {m.account.bankLabel} · {m.account.accountNumber} ·{" "}
                  {m.account.accountingAccountCode} —{" "}
                  {m.account.accountingAccountName}
                </td>
                <td className="p-2">
                  {m.counterpartRut ?? "—"} {m.counterpartName ?? "—"}
                </td>
                <td className="p-2">
                  <Link
                    className="underline"
                    to={`/contabilidad/bancos/importaciones/${m.batchId}`}
                  >
                    Importación
                  </Link>
                  <p>
                    {m.createdBy} · {m.createdAt}
                  </p>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <BankingPager
        pending={request.pending}
        next={page?.nextCursor}
        onReset={() => load()}
        onNext={() => load(page?.nextCursor ?? undefined)}
      />
    </div>
  );
}
